// mailService.js
// Sends status update reminder emails via Microsoft Graph /me/sendMail
// Requires Mail.Send scope in authConfig.js

const GRAPH_BASE = "https://graph.microsoft.com/v1.0";

// ACL - users who can send reminders. Key is display name (must match TCC owner names).
// Value is email address. Add names here to grant access and enable email resolution.
export const REMINDER_ACL = {
  "Dan Frasco":       "dan@tripleco.com",
  "Vanessa Fucciani": "vanessa@tripleco.com",
  "Martin Lopez":     "martin@tripleco.com",
  "Mike Dansby":      "mdansby@tripleco.com",
  "Rachel Hopper":    "rachel@tripleco.com",
  "Dave Smith":       "dave@tripleco.com",
  "Sab Guerriero":    "sab@tripleco.com",
};

function findInDirectory(name, orgUsers) {
  if (!orgUsers || orgUsers.length === 0) return null;
  const lower = name.toLowerCase();
  return orgUsers.find(function (u) { return u.displayName && u.displayName.toLowerCase() === lower; }) || null;
}

// Resolve owner name(s) to email addresses. orgUsers (from fetchOrgUsers,
// via OrgDirectoryContext) is checked first; REMINDER_ACL is the fallback
// -- used directly when orgUsers is empty/omitted, and per-name whenever
// a name isn't found in the live directory.
export function resolveOwnerEmails(ownerField, orgUsers) {
  if (!ownerField) return [];
  const names = ownerField.split(/[/,]/).map(function(n) { return n.trim(); });
  const emails = [];
  for (const name of names) {
    const liveMatch = findInDirectory(name, orgUsers);
    if (liveMatch) {
      emails.push({ name: liveMatch.displayName, email: liveMatch.mail });
      continue;
    }
    if (REMINDER_ACL[name]) {
      emails.push({ name: name, email: REMINDER_ACL[name] });
    } else {
      // Try partial match
      const key = Object.keys(REMINDER_ACL).find(function(k) {
        return k.toLowerCase().includes(name.toLowerCase()) ||
               name.toLowerCase().includes(k.toLowerCase().split(' ')[0]);
      });
      if (key) emails.push({ name: key, email: REMINDER_ACL[key] });
    }
  }
  return emails;
}

// Check if signed-in user is on the ACL
export function isOnAcl(account) {
  if (!account) return false;
  const email = account.username || account.email || '';
  const name = account.name || '';
  return Object.values(REMINDER_ACL).includes(email.toLowerCase()) ||
         Object.keys(REMINDER_ACL).some(function(k) {
           return k.toLowerCase() === name.toLowerCase();
         });
}

// Send a status update reminder email
export async function sendStatusReminder(accessToken, params) {
  const { toRecipients, ccRecipients, items, senderName } = params;

  // Build item list HTML
  const itemRows = items.map(function(item) {
    const statusColor = {
      'done': '#27ae60',
      'in-progress': '#e67e22',
      'not-started': '#6b7280',
      'at-risk': '#c0392b',
      'deferred': '#8e44ad',
    }[item.status] || '#6b7280';

    return `
      <tr style="border-bottom: 1px solid #e5e7eb;">
        <td style="padding: 10px 14px; font-size: 13px; color: #1a1a2e;">${item.task}</td>
        <td style="padding: 10px 14px; font-size: 11px; color: #6b7280;">${item.track || ''}</td>
        <td style="padding: 10px 14px;">
          <span style="background: ${statusColor}22; color: ${statusColor}; font-weight: 700; font-size: 11px; padding: 2px 8px; border-radius: 4px;">
            ${item.status.replace(/-/g, ' ').toUpperCase()}
          </span>
        </td>
        <td style="padding: 10px 14px; font-size: 12px; color: #6b7280;">${item.due || 'TBD'}</td>
      </tr>
    `;
  }).join('');

  const subject = `TCC Data Platform - Status Update Request (${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})`;

  const body = `
    <div style="font-family: system-ui, -apple-system, sans-serif; max-width: 680px; margin: 0 auto;">
      <div style="background: #1a1a2e; padding: 20px 24px; border-radius: 8px 8px 0 0;">
        <div style="color: #c9a84c; font-size: 10px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; margin-bottom: 6px;">Triple Crown Consulting</div>
        <div style="color: #ffffff; font-size: 20px; font-weight: 700;">Data and Intelligence Platform</div>
        <div style="color: #9ca3af; font-size: 12px; margin-top: 4px;">Status Update Request</div>
      </div>
      <div style="background: #f8f7f4; padding: 20px 24px; border: 1px solid #e5e7eb; border-top: none; border-radius: 0 0 8px 8px;">
        <p style="font-size: 14px; color: #374151; margin: 0 0 16px 0;">
          Hi,<br><br>
          ${senderName} is requesting a status update on the following item${items.length > 1 ? 's' : ''} 
          from the TCC Data Platform tracker. Please update the status in SharePoint or reply to this email 
          with the latest information.
        </p>
        <table style="width: 100%; border-collapse: collapse; background: #ffffff; border-radius: 6px; overflow: hidden; border: 1px solid #e5e7eb; margin-bottom: 16px;">
          <thead>
            <tr style="background: #1a1a2e;">
              <th style="padding: 10px 14px; text-align: left; font-size: 11px; color: #c9a84c; font-weight: 700; text-transform: uppercase; letter-spacing: 1px;">Task</th>
              <th style="padding: 10px 14px; text-align: left; font-size: 11px; color: #c9a84c; font-weight: 700; text-transform: uppercase; letter-spacing: 1px;">Track</th>
              <th style="padding: 10px 14px; text-align: left; font-size: 11px; color: #c9a84c; font-weight: 700; text-transform: uppercase; letter-spacing: 1px;">Current Status</th>
              <th style="padding: 10px 14px; text-align: left; font-size: 11px; color: #c9a84c; font-weight: 700; text-transform: uppercase; letter-spacing: 1px;">Due</th>
            </tr>
          </thead>
          <tbody>${itemRows}</tbody>
        </table>
        <div style="background: #fff3cd; border: 1px solid #fde68a; border-radius: 6px; padding: 12px 14px; font-size: 12px; color: #78350f;">
          <strong>To update:</strong> <a href="https://datamvps.tripleco.com" style="color: #c9a84c;">Open TCC Platform</a> &nbsp;|&nbsp; <a href="https://triplecc.sharepoint.com/:x:/r/sites/OperationsITHub/_layouts/15/Doc.aspx?sourcedoc=%7BE8EE616D-F19E-4739-870B-63D1C7FCD93A%7D&file=TCC_Status.xlsx&action=default&mobileredirect=true" style="color: #c9a84c;">Update TCC_Status.xlsx</a>
        </div>
        <p style="font-size: 11px; color: #9ca3af; margin: 16px 0 0 0;">
          Sent via TCC Data and Intelligence Platform · ${new Date().toLocaleString()}
        </p>
      </div>
    </div>
  `;

  const message = {
    subject: subject,
    importance: "normal",
    body: { contentType: "HTML", content: body },
    toRecipients: toRecipients.map(function(r) {
      return { emailAddress: { address: r.email, name: r.name } };
    }),
    ccRecipients: (ccRecipients || []).map(function(r) {
      return { emailAddress: { address: r.email, name: r.name } };
    }),
  };

  const res = await fetch(GRAPH_BASE + "/me/sendMail", {
    method: "POST",
    headers: {
      Authorization: "Bearer " + accessToken,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ message: message, saveToSentItems: true }),
  });

  if (!res.ok) {
    const err = await res.json().catch(function() { return {}; });
    throw new Error("Mail send failed: " + (err.error?.message || res.statusText));
  }
  return true;
}
